import { combineLatest, map, type Observable } from 'rxjs';
import { usersOperations } from '@ardoq/core';
import type { HasViewInstanceId } from '@ardoq/graph';
import type { I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { graphGroupingRules$ } from 'streams/grouping/graphGroupingRules$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import type { ViewpointModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import type { FieldWithPermissions } from 'streams/fields/getFieldsForViewpoint';
import type { HierarchicalTreemapNode } from '../types';
import { toTreemapNodes } from '../viewModel/fromScopeData';
import {
  numericFieldsInScope,
  readFieldValueFromScopeData,
} from 'streams/fields/numericFieldsInScope';
import { toSizing } from '../viewModel/toSizing';
import { sizeNodes } from '../viewModel/sizeNodes';
import { toFormatNodeValue } from '../viewModel/formatNodeValue';
import type {
  HierarchicalViewpointViewId,
  HierarchicalViewpointViewProperties,
  ViewpointSettingsFor,
} from './types';

type ViewpointScope = {
  nodes: HierarchicalTreemapNode[];
  numericFields: FieldWithPermissions[];
  readFieldValue: ReturnType<typeof readFieldValueFromScopeData>;
  i18nLocale: I18nLocale;
};

/**
 * Everything the view model derives from the viewpoint rather than from the settings panel.
 *
 * Kept in its own stream because the settings emit far more often than any of these do, and
 * rebuilding the graph for them is not cheap: `toTreemapNodes` regroups the whole viewpoint, and
 * `numericFieldsInScope` walks every component in it. Maximum depth is a slider that neither
 * function reads, so dragging it through its range once would otherwise rebuild the same tree
 * eleven times over. What is left downstream is a re-sum and a re-sort of a tree that already
 * exists.
 */
const viewpointScope$ = (
  viewId: HierarchicalViewpointViewId
): Observable<ViewpointScope> =>
  combineLatest([
    loadedGraph$,
    graphGroupingRules$,
    formattingAndLabelRules$,
    orgUsersById$,
    currentUserPermissionContext$,
    i18nLocale$,
  ]).pipe(
    map(
      ([
        loadedGraph,
        groupingRules,
        formattingRules,
        orgUsersById,
        permissionContext,
        i18nLocale,
      ]): ViewpointScope => {
        const { scopeData } = loadedGraph;
        return {
          nodes: toTreemapNodes({
            loadedGraph,
            groupingRules,
            formattingFilters: formattingRules.rules,
            formatUserFieldFn: value =>
              usersOperations.formatUserField(orgUsersById, value),
            viewId,
            i18nLocale,
          }),
          numericFields: numericFieldsInScope(scopeData, permissionContext),
          readFieldValue: readFieldValueFromScopeData(scopeData),
          i18nLocale,
        };
      }
    )
  );

export type GetViewpointViewModelArgs<Id extends HierarchicalViewpointViewId> =
  HasViewInstanceId & {
    viewId: Id;
    viewSettingsWithChanges$: Observable<ViewpointModeInstanceViewSettings<Id>>;
  };

/**
 * What these views need in viewpoint mode, which is markedly less than in workspace mode: no
 * context, no scenario, no traversal configuration and no streamed component collection. A
 * viewpoint has already decided what is in scope, so the whole view model is its loaded graph,
 * the grouping the user chose, and the field to size by.
 *
 * `toSizing` runs once here and the answer is carried on the properties. It is the only thing
 * that decides what `sizeByFieldName` means — a name has to be among the fields the view offers
 * and not confidential — and the settings panel's display value and the label number format both
 * used to ask the raw setting the same question and get a different answer. A count sized tree
 * labelled in a refused currency field's format reads as an amount of money.
 */
export const getViewpointViewModel$ = <Id extends HierarchicalViewpointViewId>({
  viewId,
  viewSettingsWithChanges$,
}: GetViewpointViewModelArgs<Id>): Observable<
  HierarchicalViewpointViewProperties<ViewpointSettingsFor<Id>>
> =>
  combineLatest([
    viewSettingsWithChanges$.pipe(
      map(({ currentSettings }) => currentSettings)
    ),
    viewpointScope$(viewId),
    locale$,
  ]).pipe(
    map(
      ([
        viewSettings,
        { nodes, numericFields, readFieldValue, i18nLocale },
        locale,
      ]) => {
        const sizing = toSizing(viewSettings.sizeByFieldName, numericFields);

        return {
          viewSettings: viewSettings as ViewpointSettingsFor<Id>,
          sizedNodes: sizeNodes({ nodes, sizing, readFieldValue }),
          numericFields,
          sizing,
          formatValue: toFormatNodeValue({ locale, numericFields, sizing }),
          i18nLocale,
        };
      }
    )
  );
